import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import {
  Megaphone, AlertTriangle, Info, Zap, Plus, Trash2, X, Eye, EyeOff,
  CheckCircle2, Loader2,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

const PUBLISHER_ROLES = new Set(["admin", "national_aftersales_manager"]);

const PRIORITY_META = {
  info:    { label: "Info",    Icon: Info,          chip: "bg-sky-50 text-sky-700 border-sky-200",         bar: "border-l-sky-400" },
  warning: { label: "Aviso",   Icon: AlertTriangle, chip: "bg-amber-50 text-amber-700 border-amber-200",   bar: "border-l-amber-400" },
  urgent:  { label: "Urgente", Icon: Zap,           chip: "bg-rose-50 text-rose-700 border-rose-200",      bar: "border-l-rose-500" },
};

function PriorityChip({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.info;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border ${m.chip}`} data-testid={`priority-${priority}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const canPublish = user && PUBLISHER_ROLES.has(user.role);

  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("info");

  async function load() {
    try {
      const r = await api.get("/announcements");
      setItems(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha a carregar anúncios");
      setItems([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function markRead(id) {
    try {
      await api.post(`/announcements/${id}/read`);
      setItems((prev) => (prev || []).map((a) => a.id === id ? { ...a, is_read: true, read_count: (a.read_count || 0) + (a.is_read ? 0 : 1) } : a));
      window.dispatchEvent(new CustomEvent("rcc:announcement-read"));
    } catch {}
  }

  async function markAllRead() {
    try {
      await api.post("/announcements/read-all");
      setItems((prev) => (prev || []).map((a) => ({ ...a, is_read: true })));
      window.dispatchEvent(new CustomEvent("rcc:announcement-read"));
    } catch {}
  }

  async function remove(id) {
    if (!window.confirm("Apagar este anúncio para toda a equipa?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      setItems((prev) => (prev || []).filter((a) => a.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha a apagar");
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post("/announcements", { title: title.trim(), body, priority });
      setTitle(""); setBody(""); setPriority("info"); setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha a publicar");
    } finally {
      setBusy(false);
    }
  }

  const unreadCount = useMemo(() => (items || []).filter((a) => !a.is_read).length, [items]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <PageHeader
        overline="Comunicação interna"
        title="Mural do Distribuidor"
        subtitle="Anúncios oficiais da Ronil Maputo para toda a equipa. Fica a par de novas políticas, celebrações e actualizações."
      />


      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-sm text-slate-500" data-testid="announcements-count">
          {items === null ? "A carregar…" : `${items.length} anúncio${items.length === 1 ? "" : "s"}`}
          {unreadCount > 0 && <span className="ml-2 inline-flex items-center gap-1 text-ronil-navy font-heading font-semibold"><Eye className="w-3.5 h-3.5" /> {unreadCount} por ler</span>}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-slate-600 hover:text-ronil-navy inline-flex items-center gap-1"
              data-testid="mark-all-read-btn"
            >
              <CheckCircle2 className="w-4 h-4" /> Marcar tudo como lido
            </button>
          )}
          {canPublish && (
            <button
              onClick={() => setShowForm((s) => !s)}
              className="bg-ronil-navy hover:bg-ronil-navy-hover text-white px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm hover:shadow-elevation transition-all"
              data-testid="new-announcement-btn"
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showForm ? "Cancelar" : "Novo anúncio"}
            </button>
          )}
        </div>
      </div>

      {showForm && canPublish && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-5 shadow-soft-card mb-6" data-testid="announcement-form">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3 mb-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do anúncio"
              maxLength={200}
              required
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-heading font-semibold focus:outline-none focus:ring-2 focus:ring-ronil-navy/20 focus:border-ronil-navy"
              data-testid="announcement-title"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ronil-navy/20 focus:border-ronil-navy"
              data-testid="announcement-priority"
            >
              <option value="info">Info</option>
              <option value="warning">Aviso</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={"Conteúdo em markdown.\n\n**Negrito**, *itálico*, listas, links e imagens via URL:\n![cartaz](https://intranet.ronil.co.mz/cartaz.jpg)"}
            maxLength={20000}
            rows={8}
            required
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ronil-navy/20 focus:border-ronil-navy"
            data-testid="announcement-body"
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-slate-500">Suporta Markdown. Imagens via URL público (intranet, Google Drive, etc.).</p>
            <button
              type="submit"
              disabled={busy || !title.trim() || !body.trim()}
              className="bg-ronil-navy hover:bg-ronil-navy-hover disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 transition-all"
              data-testid="announcement-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              Publicar
            </button>
          </div>
          {error && <p className="text-xs text-rose-600 mt-2">{error}</p>}
        </form>
      )}

      {items === null ? (
        <div className="p-12 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
          <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Ainda não há anúncios publicados.</p>
          {canPublish && <p className="text-xs text-slate-400 mt-1">Clica em <span className="font-semibold">Novo anúncio</span> para publicar o primeiro.</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((a) => {
            const meta = PRIORITY_META[a.priority] || PRIORITY_META.info;
            return (
              <article
                key={a.id}
                className={`bg-white border border-slate-200 border-l-4 ${meta.bar} rounded-lg shadow-soft-card p-5 transition-all ${!a.is_read ? "ring-1 ring-ronil-navy/10" : ""}`}
                data-testid={`announcement-${a.id}`}
              >
                <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <PriorityChip priority={a.priority} />
                      {!a.is_read && <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-widest text-ronil-navy bg-ronil-navy-50 px-2 py-0.5 rounded-md">Novo</span>}
                    </div>
                    <h3 className="font-display text-xl font-bold text-ronil-navy leading-tight" data-testid={`announcement-title-${a.id}`}>{a.title}</h3>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Por <span className="font-heading font-semibold text-slate-700">{a.author?.name || "—"}</span>
                      {" · "}{fmtDate(a.created_at)}
                      {typeof a.read_count === "number" && <span className="ml-2 text-slate-400">· {a.read_count} leitura{a.read_count === 1 ? "" : "s"}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!a.is_read ? (
                      <button
                        onClick={() => markRead(a.id)}
                        className="text-xs text-slate-500 hover:text-ronil-navy inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-50"
                        data-testid={`mark-read-${a.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Marcar lido
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 inline-flex items-center gap-1 px-2 py-1"><EyeOff className="w-3.5 h-3.5" /> Lido</span>
                    )}
                    {canPublish && (
                      <button
                        onClick={() => remove(a.id)}
                        className="text-xs text-slate-400 hover:text-rose-600 p-1 rounded-md hover:bg-rose-50"
                        title="Apagar"
                        data-testid={`delete-announcement-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </header>
                <div className="prose prose-sm max-w-none text-slate-700 announcement-body" data-testid={`announcement-body-${a.id}`}>
                  <ReactMarkdown
                    components={{
                      img: (props) => <img {...props} className="rounded-md border border-slate-200 max-h-[480px] w-auto" loading="lazy" alt={props.alt || ""} />,
                      a: (props) => <a {...props} target="_blank" rel="noreferrer" className="text-ronil-navy underline underline-offset-2" />,
                    }}
                  >{a.body}</ReactMarkdown>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
